import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CardAvatar } from '@sorare/core/src/components/card/Avatar';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';

import { So5AppearanceScore } from 'components/appearance/So5AppearanceScore';
import { Captain } from 'components/so5/Captain';
import { isGameLive } from 'lib/so5';

import type { RankingAppearances_so5Ranking } from './__generated__/index.graphql';

type Props = {
  so5Ranking: RankingAppearances_so5Ranking;
};

const AvatarWrapper = styled.div`
  position: relative;
`;

const CaptainWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  left: calc(-1 * var(--unit));
`;

const Appearances = styled(Horizontal)`
  --border-radius: var(--unit);
  align-items: flex-start;
  display: grid;

  & > * {
    flex-grow: 1;
  }
`;

const StyledCardAvatar = styled(CardAvatar)`
  & > img {
    max-width: 100%;
    width: 100px;
    aspect-ratio: 1;
  }
`;

export const RankingAppearances = ({ so5Ranking }: Props) => {
  const { so5Lineup } = so5Ranking;
  const { so5Appearances } = so5Lineup;

  return (
    <Appearances
      style={{ gridTemplateColumns: `repeat(${so5Appearances.length}, 1fr)` }}
    >
      {so5Appearances.map(appearance => (
        <Vertical key={appearance.id} center>
          {!!appearance.anyCard && (
            <AvatarWrapper>
              <StyledCardAvatar key={appearance.id} card={appearance.anyCard} />
              {!!appearance.captain && (
                <CaptainWrapper>
                  <Captain active size={2} />
                </CaptainWrapper>
              )}
            </AvatarWrapper>
          )}
          <Horizontal gap={0.5}>
            {appearance.eligiblePlayerGameScores.some(pgs =>
              isGameLive(pgs.anyGame.statusTyped)
            ) && <LiveDot size="sm" animate />}
            <So5AppearanceScore
              so5Appearance={appearance}
              hideReviewing
              withDate
            />
          </Horizontal>
        </Vertical>
      ))}
    </Appearances>
  );
};

RankingAppearances.fragments = {
  so5Ranking: gql`
    fragment RankingAppearances_so5Ranking on So5Ranking {
      id
      so5Lineup {
        id
        so5Appearances {
          id
          captain
          anyCard {
            slug
            ...CardAvatar_anyCard
          }
          eligiblePlayerGameScores {
            id
            anyGame {
              id
              statusTyped
            }
          }
          ...So5AppearanceScore_so5Appearance
        }
      }
    }
    ${CardAvatar.fragments.anyCard}
    ${So5AppearanceScore.fragments.so5Appearance}
  ` as TypedDocumentNode<RankingAppearances_so5Ranking>,
};
